import type { CSSProperties } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBookOpen, faEye } from "@fortawesome/free-solid-svg-icons";
import { lessonIconDefinition } from "./lessonIcons";
import { textColorsForBackground } from "./lessonCardColors";

export interface PresentationPreviewTileProps {
  readonly presentationId: string;
  readonly presentationTitle: string;
  readonly courseId: string;
  readonly cohortId?: string;
  readonly returnTo?: string;
  readonly iconName?: string | null;
  readonly iconBgColor?: string | null;
  readonly iconFgColor?: string | null;
  readonly size?: "sm" | "md";
}

const DEFAULT_BG = "#1b4d2e";

function previewUrl(args: {
  courseId: string;
  presentationId: string;
  cohortId?: string;
  returnTo?: string;
}): string {
  if (args.cohortId) {
    const base = `/teacher/cohorts/${encodeURIComponent(args.cohortId)}/presentations/${encodeURIComponent(args.presentationId)}/preview`;
    return args.returnTo ? `${base}?returnTo=${encodeURIComponent(args.returnTo)}` : base;
  }
  return `/teacher/courses/${encodeURIComponent(args.courseId)}/presentations/${encodeURIComponent(args.presentationId)}/preview`;
}

export function PresentationPreviewTile({
  presentationId,
  presentationTitle,
  courseId,
  cohortId,
  returnTo,
  iconName,
  iconBgColor,
  iconFgColor,
  size = "sm",
}: PresentationPreviewTileProps) {
  const href = previewUrl({ courseId, presentationId, cohortId, returnTo });
  const background = iconBgColor?.trim() || DEFAULT_BG;
  const colors = textColorsForBackground(background);
  const watermarkColor = iconFgColor?.trim() || colors.title;
  const icon = lessonIconDefinition(iconName) ?? faBookOpen;

  return (
    <Link
      to={href}
      className={["lesson-preview-tile", `lesson-preview-tile--${size}`].join(" ")}
      data-tone={colors.tone}
      style={{ "--lesson-preview-bg": background } as CSSProperties}
      title={`Preview ${presentationTitle}`}
      aria-label={`Preview presentation ${presentationTitle}`}
    >
      <span className="lesson-preview-tile-watermark" style={{ color: watermarkColor }} aria-hidden>
        <FontAwesomeIcon icon={icon} fixedWidth />
      </span>
      <span className="lesson-preview-tile-play" aria-hidden>
        <FontAwesomeIcon icon={faEye} />
      </span>
    </Link>
  );
}
